import { Modal, ModalBody } from '@carbon/react'
import { useCreateAnnouncement } from '@sphere/communication/data-access'
import {
  AnnouncementTargetType,
  mapAnnouncementFormToCreateDto,
  type AnnouncementFormData,
} from '@sphere/communication/domain'
import { useEstablishments } from '@sphere/school/data-access'
import { useToast } from '@sphere/shared/util'
import {
  ANNOUNCEMENT_CREATE_FORM_ID,
  AnnouncementCreateForm,
} from './AnnouncementCreateForm'

export interface AnnouncementCreateModalProps {
  open: boolean
  onClose: () => void
  scopeEstablishmentId?: string
}

export function AnnouncementCreateModal({
  open,
  onClose,
  scopeEstablishmentId = '',
}: AnnouncementCreateModalProps) {
  const { toast } = useToast()
  const { data: establishments } = useEstablishments()
  const createMutation = useCreateAnnouncement()

  const handleValidSubmit = (data: AnnouncementFormData) => {
    const needsScopeEstablishment =
      data.targetType === AnnouncementTargetType.ALL ||
      data.targetType === AnnouncementTargetType.ROLE

    if (needsScopeEstablishment && !scopeEstablishmentId) {
      toast({
        variant: 'destructive',
        title: 'Erreur de configuration',
        description:
          "Vous n'êtes rattaché à aucun établissement. Veuillez contacter un administrateur.",
      })
      return
    }

    const dto = mapAnnouncementFormToCreateDto(data, scopeEstablishmentId)
    createMutation.mutate(dto, {
      onSuccess: () => {
        toast({ title: 'Annonce publiée' })
        onClose()
      },
      onError: () => {
        toast({
          variant: 'destructive',
          title: 'Erreur lors de la publication',
        })
      },
    })
  }

  const requestFormSubmit = () => {
    const el = document.getElementById(
      ANNOUNCEMENT_CREATE_FORM_ID
    ) as HTMLFormElement | null
    el?.requestSubmit()
  }

  return (
    <Modal
      open={open}
      onRequestClose={onClose}
      modalHeading="Nouvelle annonce"
      size="md"
      primaryButtonText={
        createMutation.isPending ? 'Publication...' : 'Publier'
      }
      secondaryButtonText="Annuler"
      onRequestSubmit={event => {
        event.preventDefault()
        requestFormSubmit()
      }}
      primaryButtonDisabled={createMutation.isPending}
    >
      <ModalBody>
        <p style={{ marginBottom: '1rem' }}>
          Votre message sera visible par les utilisateurs ciblés.
        </p>
        <AnnouncementCreateForm
          isActive={open}
          defaultEstablishmentId={scopeEstablishmentId}
          establishments={establishments}
          onSubmit={handleValidSubmit}
        />
      </ModalBody>
    </Modal>
  )
}
